/**
 * ProblemBand — one rendering for every refusal (mission 5 FR-020; mission 6
 * WP03 T022; FR-016, FR-026, C-006; research §1.9; contracts/ui-api.md
 * "Problem gate table").
 *
 * Precedence: the screen's `override`, then `GATE_RENDERING`, then
 * `failureCopy()`, all inside `resolveProblem`. The band names the remedy in
 * words, linked when the row gives a destination, and `data-requirement`
 * carries the requirement. It never keys on `detail`: only the B.3 and P.4
 * rows show it beneath the remedy, and `try-again` renders it verbatim (its
 * remedy is the detail). A row with no remedy (X.10, TX.4) shows the
 * problem's title. `reserved` (TX.4, a later mission) renders as info.
 *
 * Mission 8 WP07 T035 (OR-11, U2; research §1.3): `warning` (the assistant is
 * busy or unavailable) renders `Band kind="warning"`, a polite `role="status"`;
 * blocked keeps `role="alert"` and info stays `role="status"`.
 */
import { Link } from 'react-router-dom'

import type { Problem } from '@/lib/api'
import { resolveProblem, type ProblemOverrides, type ResolvedProblem } from '@/lib/problems'

import { Band, type BandKind } from './Band'

const KIND: Record<ResolvedProblem['band'], BandKind> = {
  blocked: 'blocked',
  warning: 'warning',
  info: 'info',
  reserved: 'info',
}

export interface ProblemBandProps {
  problem: Problem
  override?: ProblemOverrides | undefined
}

export function ProblemBand({ problem, override }: ProblemBandProps) {
  const resolved = resolveProblem(problem, override)
  const lead = resolved.remedy ?? problem.title
  return (
    <Band kind={KIND[resolved.band]} requirement={resolved.requirement} data-band={resolved.band}>
      <p>
        {resolved.to ? (
          <Link to={resolved.to} className="text-[var(--color-link)] underline hover:text-[var(--color-link-hover)]">
            {lead}
          </Link>
        ) : (
          lead
        )}
      </p>
      {resolved.detail ? <p className="mt-1 text-body-sm text-navy-700">{resolved.detail}</p> : null}
    </Band>
  )
}
